import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import {
  api,
  ApiError,
  type BlockKind,
  type BlockTextColor,
  type BlockTextSize,
  type Item,
  type ItemBlock,
} from '../../lib/api'
import { invaliderFiches } from '../../lib/cache'
import { ChoixDImage } from './ChoixDImage'
import {
  cibleDuDeplacement,
  decalageVoisin,
  deplacer,
  type Empreinte,
} from '../spaces/ordreParGlissement'
import { copier, decouperEnLiens } from '../../lib/liens'

/**
 * Les blocs d'une fiche : un paragraphe, une liste à cocher, une image, un lien.
 *
 * Une <b>pile verticale</b> qu'on réordonne, et non une toile où l'on pose les choses où l'on
 * veut. Positionner en deux dimensions au doigt est imprécis, et une mise en page composée sur un
 * écran large arriverait cassée sur un téléphone de 390 px — qui est l'écran principal. L'ordre,
 * lui, se tient sur les deux.
 *
 * Une fiche sans bloc n'affiche qu'un bouton d'ajout discret : la richesse est disponible
 * partout, et n'encombre nulle part tant qu'on ne s'en sert pas.
 */
export function Blocs({
  spaceId,
  itemId,
  blocs,
}: {
  spaceId: string
  itemId: string
  blocs: readonly ItemBlock[]
}) {
  const queryClient = useQueryClient()
  const [erreur, setErreur] = useState<string | null>(null)

  // Ce qu'on tient, et de combien on l'a déplacé. L'ordre n'est recalculé qu'au relâchement :
  // réorganiser la liste à chaque image faisait bouger le sol sous le doigt, et la cible
  // oscillait entre deux rangs (correction S89).
  const [tenu, setTenu] = useState<{
    id: string
    depuis: number
    dy: number
    cible: number
    hauteur: number
    empreintes: readonly Empreinte[]
  } | null>(null)

  const lignes = useRef(new Map<string, HTMLLIElement>())
  const depart = useRef(0)

  // Les titres en cours de frappe. Le champ est contrôlé par ce qu'on tape, enregistré à la
  // sortie : une requête par caractère saturerait un réseau de téléphone.
  const [titres, setTitres] = useState<Record<string, string>>({})

  // Quels blocs sont ouverts à la retouche. Un bloc rempli s'affiche comme du texte : laisser
  // un formulaire ouvert en permanence donnait une fiche qui ressemble à un panneau de réglages,
  // et trois paragraphes de suite prenaient trois fois la place qu'il fallait (correction S90).
  //
  // Cette liste est la seule vérité. Elle a d'abord cohabité avec un « ce bloc est vide donc il
  // est ouvert », et les deux se contredisaient : un bloc qu'on venait de remplir restait « vide »
  // aux yeux du calcul — le texte tapé n'est enregistré qu'à la sortie du champ — et la coche de
  // validation ne s'affichait jamais (correction S94).
  const [enEdition, setEnEdition] = useState<readonly string[]>([])
  const connus = useRef(new Set<string>())

  // Le texte en cours de frappe, tenu ici et non dans le bloc lui-même.
  //
  // Il n'était enregistré qu'à la sortie du champ — et sur un écran tactile, toucher la coche ne
  // fait pas sortir du champ : un bouton n'y prend pas le focus. On refermait donc le bloc sans
  // que rien ne parte, et le texte était perdu (correction S95). La coche l'enregistre
  // maintenant elle-même.
  const [brouillons, setBrouillons] = useState<Record<string, string>>({})

  // Replié : le contenu est caché, le titre reste. Une fiche qui porte dix blocs se parcourt
  // alors d'un coup d'œil.
  const [replies, setReplies] = useState<readonly string[]>([])

  // Un bloc qui vient d'apparaître et qui est vide s'ouvre de lui-même : on ne l'ajoute pas pour
  // le regarder vide. Une seule fois, à son arrivée — ensuite c'est la liste qui décide.
  useEffect(() => {
    const nouveaux = blocs.filter((bloc) => !connus.current.has(bloc.id))

    if (nouveaux.length === 0) {
      return
    }

    for (const bloc of nouveaux) {
      connus.current.add(bloc.id)
    }

    const aOuvrir = nouveaux.filter(estVide).map((bloc) => bloc.id)

    if (aOuvrir.length > 0) {
      setEnEdition((actuels) => [...actuels, ...aOuvrir])
    }
  }, [blocs])

  const ouvert = (bloc: ItemBlock) => enEdition.includes(bloc.id)

  /**
   * Ouvre la retouche, ou la referme en enregistrant ce qui a été tapé.
   *
   * L'enregistrement est fait ici plutôt que par le champ : c'est le seul endroit qui survit à
   * la fermeture du bloc.
   */
  const basculer = async (bloc: ItemBlock) => {
    if (!enEdition.includes(bloc.id)) {
      setEnEdition((actuels) => [...actuels, bloc.id])
      return
    }

    const brouillon = brouillons[bloc.id]
    const titre = titres[bloc.id]

    const texteChange = brouillon !== undefined && brouillon !== (bloc.text ?? '')
    const titreChange = titre !== undefined && titre.trim() !== (bloc.title ?? '')

    if (texteChange || titreChange) {
      try {
        await api.updateBlock(spaceId, itemId, bloc.id, {
          text: texteChange ? brouillon : bloc.text,
          url: bloc.url,
          attachmentId: bloc.attachmentId,
          title: titreChange ? titre.trim() : bloc.title,
          textSize: bloc.textSize,
          textColor: bloc.textColor,
        })

        // Le brouillon s'efface une fois parti : laissé en place, il continuerait d'écraser ce
        // que le serveur vient de confirmer (correction S96).
        setBrouillons(({ [bloc.id]: _, ...reste }) => reste)
        setTitres(({ [bloc.id]: _, ...reste }) => reste)

        await rafraichir()
      } catch (cause) {
        echoue(cause)
        return
      }
    }

    setEnEdition((actuels) => actuels.filter((id) => id !== bloc.id))
  }

  const affiches = blocs

  const rafraichir = () => invaliderFiches(queryClient, spaceId)

  const echoue = (cause: unknown) =>
    setErreur(cause instanceof ApiError ? cause.message : "L'opération a échoué.")

  const ajouter = useMutation({
    mutationFn: (kind: BlockKind) => api.addBlock(spaceId, itemId, { kind }),
    onSuccess: rafraichir,
    onError: echoue,
  })

  const reordonner = useMutation({
    mutationFn: (ids: readonly string[]) => api.reorderBlocks(spaceId, itemId, ids),
    onSuccess: rafraichir,
    onError: echoue,
  })

  /** L'empreinte des blocs au moment où le geste commence : elle ne bougera plus. */
  const prendreLEmpreinte = (identifiant: string) => {
    const depuis = blocs.findIndex((bloc) => bloc.id === identifiant)

    if (depuis === -1) {
      return
    }

    const empreintes = blocs.map((bloc) => {
      const boite = lignes.current.get(bloc.id)?.getBoundingClientRect()

      return { haut: boite?.top ?? 0, hauteur: boite?.height ?? 0 }
    })

    setTenu({
      id: identifiant,
      depuis,
      dy: 0,
      cible: depuis,
      hauteur: empreintes[depuis]?.hauteur ?? 0,
      empreintes,
    })
  }

  return (
    <section className="mt-4">
      {affiches.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {affiches.map((bloc, rang) => (
            <li
              key={bloc.id}
              ref={(element) => {
                if (element) {
                  lignes.current.set(bloc.id, element)
                } else {
                  lignes.current.delete(bloc.id)
                }
              }}
              className="rounded-card border border-border bg-card"
              style={{
                // Le bloc tenu suit le doigt ; les autres s'écartent pour montrer où il
                // tombera. Sans ce décalage, on déplaçait un bloc dans le vide.
                transform: tenu
                  ? `translateY(${
                      tenu.id === bloc.id
                        ? tenu.dy
                        : decalageVoisin(rang, tenu.depuis, tenu.cible, tenu.hauteur)
                    }px)`
                  : undefined,
                transition: tenu?.id === bloc.id ? 'none' : 'transform 120ms ease-out',
                zIndex: tenu?.id === bloc.id ? 1 : undefined,
                position: 'relative',
                boxShadow: tenu?.id === bloc.id ? 'var(--shadow-sheet)' : undefined,
              }}
            >
              <div className="flex items-center gap-2 border-b border-border px-2">
                {/* La poignée est un bouton à part : saisir le bloc entier empêcherait d'écrire
                    dedans, et `touch-action: none` gèlerait le défilement sur toute la fiche. */}
                <button
                  type="button"
                  aria-label="Déplacer ce bloc"
                  onPointerDown={(evenement) => {
                    evenement.currentTarget.setPointerCapture(evenement.pointerId)
                    depart.current = evenement.clientY
                    prendreLEmpreinte(bloc.id)
                  }}
                  onPointerMove={(evenement) => {
                    if (!tenu || tenu.id !== bloc.id) {
                      return
                    }

                    const dy = evenement.clientY - depart.current

                    setTenu({
                      ...tenu,
                      dy,
                      cible: cibleDuDeplacement(tenu.empreintes, tenu.depuis, dy),
                    })
                  }}
                  onPointerUp={() => {
                    if (!tenu || tenu.id !== bloc.id) {
                      return
                    }

                    const { depuis, cible } = tenu
                    setTenu(null)

                    if (cible !== depuis) {
                      reordonner.mutate(
                        deplacer(blocs, depuis, cible).map((autre) => autre.id),
                      )
                    }
                  }}
                  onPointerCancel={() => setTenu(null)}
                  onKeyDown={(evenement) => {
                    if (evenement.key !== 'ArrowUp' && evenement.key !== 'ArrowDown') {
                      return
                    }

                    evenement.preventDefault()

                    const depuis = blocs.findIndex((autre) => autre.id === bloc.id)
                    const vers = depuis + (evenement.key === 'ArrowUp' ? -1 : 1)

                    if (vers < 0 || vers >= blocs.length) {
                      return
                    }

                    reordonner.mutate(deplacer(blocs, depuis, vers).map((autre) => autre.id))
                  }}
                  className="flex items-center justify-center text-ink-muted"
                  style={{ width: 36, height: 40, touchAction: 'none', cursor: 'grab' }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
                    <circle cx="6" cy="4" r="1.4" />
                    <circle cx="10" cy="4" r="1.4" />
                    <circle cx="6" cy="8" r="1.4" />
                    <circle cx="10" cy="8" r="1.4" />
                    <circle cx="6" cy="12" r="1.4" />
                    <circle cx="10" cy="12" r="1.4" />
                  </svg>
                </button>

                {/* Le titre du bloc, donné à la main. « Cases à cocher » dit ce que c'est, pas
                    de quoi il s'agit : « Courses du week-end » vaut mieux sur une fiche qui en
                    porte plusieurs. Sans titre, la nature reprend sa place. */}
                {ouvert(bloc) ? (
                <input
                  value={titres[bloc.id] ?? bloc.title ?? ''}
                  placeholder={NOMS[bloc.kind]}
                  aria-label={`Titre de ce bloc ${NOMS[bloc.kind]}`}
                  onChange={(evenement) => setTitres((actuels) => ({
                    ...actuels,
                    [bloc.id]: evenement.target.value,
                  }))}
                  onBlur={async (evenement) => {
                    // Enregistré aussi ici, pour qui touche ailleurs sans passer par la coche.
                    // Mais la coche ne compte plus dessus : elle écrivait l'ancien titre par
                    // dessus le nouveau, le brouillon n'étant jamais effacé.
                    const propose = evenement.target.value.trim()

                    if (propose === (bloc.title ?? '')) {
                      return
                    }

                    try {
                      await api.updateBlock(spaceId, itemId, bloc.id, {
                        text: bloc.text,
                        url: bloc.url,
                        attachmentId: bloc.attachmentId,
                        textSize: bloc.textSize,
                        textColor: bloc.textColor,
                        title: propose,
                      })
                      setTitres(({ [bloc.id]: _, ...reste }) => reste)
                      await rafraichir()
                    } catch (cause) {
                      echoue(cause)
                    }
                  }}
                  className="grow bg-transparent text-label font-semibold uppercase text-ink-muted outline-none"
                  style={{ minWidth: 0 }}
                />
                ) : (
                  // Le titre replie et déplie : c'est la cible la plus large de l'en-tête, et
                  // viser une flèche de 12 px au pouce n'était pas raisonnable.
                  <button
                    type="button"
                    onClick={() =>
                      setReplies((actuels) =>
                        actuels.includes(bloc.id)
                          ? actuels.filter((id) => id !== bloc.id)
                          : [...actuels, bloc.id],
                      )
                    }
                    aria-expanded={!replies.includes(bloc.id)}
                    className="grow truncate text-left text-label font-semibold uppercase text-ink-muted"
                  >
                    {bloc.title ?? NOMS[bloc.kind]}
                  </button>
                )}

                {/* Le crayon ouvre la retouche, la coche la referme. Toujours présent : il
                    manquait sur un bloc jugé vide, c'est-à-dire précisément celui qu'on venait
                    de remplir et qu'on cherchait à refermer. */}
                {/* Replier : le titre reste, le contenu se cache. Une fiche qui porte dix blocs
                    se parcourt alors d'un coup d'œil. Indisponible pendant la retouche — on ne
                    cache pas ce qu'on est en train d'écrire. */}
                {ouvert(bloc) ? null : (
                  <button
                    type="button"
                    onClick={() =>
                      setReplies((actuels) =>
                        actuels.includes(bloc.id)
                          ? actuels.filter((id) => id !== bloc.id)
                          : [...actuels, bloc.id],
                      )
                    }
                    aria-expanded={!replies.includes(bloc.id)}
                    aria-label={replies.includes(bloc.id) ? 'Déplier ce bloc' : 'Replier ce bloc'}
                    className="text-ink-muted"
                    style={{ width: 36, height: 40, fontSize: 18 }}
                  >
                    {replies.includes(bloc.id) ? '▸' : '▾'}
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => void basculer(bloc)}
                  aria-label={ouvert(bloc) ? 'Terminer la retouche' : 'Modifier ce bloc'}
                  className="text-meta font-semibold"
                  style={{
                    width: 36,
                    height: 40,
                    color: ouvert(bloc) ? 'var(--color-accent)' : 'var(--color-ink-muted)',
                  }}
                >
                  {ouvert(bloc) ? '✓' : '✎'}
                </button>

                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await api.deleteBlock(spaceId, itemId, bloc.id)
                      await rafraichir()
                    } catch (cause) {
                      echoue(cause)
                    }
                  }}
                  aria-label="Supprimer ce bloc"
                  className="text-meta font-semibold text-ink-muted"
                  style={{ width: 36, height: 40 }}
                >
                  ✕
                </button>
              </div>

              {replies.includes(bloc.id) && !ouvert(bloc) ? null : (
                <div className="p-3">
                  <Contenu
                    spaceId={spaceId}
                    itemId={itemId}
                    bloc={bloc}
                    edition={ouvert(bloc)}
                    brouillon={brouillons[bloc.id]}
                    onBrouillon={(valeur) =>
                      setBrouillons((actuels) => ({ ...actuels, [bloc.id]: valeur }))
                    }
                    onError={echoue}
                    onDone={rafraichir}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {erreur ? <p className="mt-2 text-meta text-danger">{erreur}</p> : null}

      {/* Une grille de deux et non une rangée qui déborde : les quatre libellés sont de
          longueurs trop différentes pour tenir sur une ligne, et le dernier retombait seul
          sous les trois autres. En deux colonnes, chacun occupe la même place et la prise au
          pouce y gagne. */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        {(['text', 'checklist', 'image', 'link'] as const).map((kind) => (
          <button
            key={kind}
            type="button"
            disabled={ajouter.isPending}
            onClick={() => ajouter.mutate(kind)}
            className="rounded-field border border-dashed border-border px-3 text-meta text-ink-muted"
            style={{ height: 44 }}
          >
            + {NOMS[kind]}
          </button>
        ))}
      </div>
    </section>
  )
}

/**
 * Un bloc qu'on vient d'ajouter, encore sans contenu.
 *
 * Il s'ouvre de lui-même à la retouche : on ne crée pas un bloc pour le regarder vide.
 */
function estVide(bloc: ItemBlock): boolean {
  return (
    (bloc.text ?? '').length === 0 &&
    (bloc.url ?? '').length === 0 &&
    bloc.attachmentId === null &&
    bloc.lines.length === 0
  )
}

const NOMS: Record<BlockKind, string> = {
  text: 'Texte',
  checklist: 'Cases à cocher',
  image: 'Image',
  link: 'Lien',
}

/** Le contenu d'un bloc, selon sa nature. */
function Contenu({
  spaceId,
  itemId,
  bloc,
  edition,
  brouillon,
  onBrouillon,
  onError,
  onDone,
}: {
  spaceId: string
  itemId: string
  bloc: ItemBlock
  edition: boolean
  brouillon: string | undefined
  onBrouillon: (valeur: string) => void
  onError: (cause: unknown) => void
  onDone: () => Promise<void>
}) {
  if (bloc.kind === 'checklist') {
    return (
      <Cases
        spaceId={spaceId}
        itemId={itemId}
        bloc={bloc}
        edition={edition}
        onError={onError}
        onDone={onDone}
      />
    )
  }

  if (bloc.kind === 'image') {
    return <Image spaceId={spaceId} itemId={itemId} bloc={bloc} onError={onError} onDone={onDone} />
  }

  if (bloc.kind === 'link') {
    return <Lien spaceId={spaceId} itemId={itemId} bloc={bloc} onError={onError} onDone={onDone} />
  }

  return (
    <Texte
      spaceId={spaceId}
      itemId={itemId}
      bloc={bloc}
      edition={edition}
      brouillon={brouillon}
      onBrouillon={onBrouillon}
      onError={onError}
      onDone={onDone}
    />
  )
}

/**
 * Un paragraphe.
 *
 * Enregistré à la sortie du champ et non à chaque frappe : une requête par caractère saturerait
 * un réseau de téléphone, et l'autre verrait le texte s'écrire mot à mot.
 */
function Texte({
  spaceId,
  itemId,
  bloc,
  edition,
  brouillon,
  onBrouillon,
  onError,
  onDone,
}: {
  spaceId: string
  itemId: string
  bloc: ItemBlock
  edition: boolean
  /** Ce qui est tapé, tenu par le parent : c'est lui qui survit à la fermeture du bloc. */
  brouillon: string | undefined
  onBrouillon: (valeur: string) => void
  onError: (cause: unknown) => void
  onDone: () => Promise<void>
}) {
  const texte = brouillon ?? bloc.text ?? ''

  const enregistrer = async (champs: {
    text?: string
    textSize?: BlockTextSize
    textColor?: BlockTextColor | null
  }) => {
    try {
      await api.updateBlock(spaceId, itemId, bloc.id, {
        text: champs.text ?? bloc.text,
        title: bloc.title,
        textSize: champs.textSize ?? bloc.textSize,
        textColor: champs.textColor === undefined ? bloc.textColor : champs.textColor,
      })
      await onDone()
    } catch (cause) {
      onError(cause)
    }
  }

  if (!edition) {
    // Lu : du texte, et rien d'autre. Les adresses qu'il contient restent cliquables, comme
    // dans le texte d'une fiche — ce qui est un lien pour qui l'écrit doit en être un.
    return (
      <p
        style={{
          fontSize: TAILLES[bloc.textSize ?? 'normal'],
          color: bloc.textColor ? `var(--color-texte-${bloc.textColor})` : undefined,
          whiteSpace: 'pre-wrap',
          maxWidth: '60ch',
        }}
      >
        {decouperEnLiens(bloc.text ?? '').map((fragment, rang) =>
          fragment.lien ? (
            <a
              key={rang}
              href={fragment.lien}
              target="_blank"
              rel="noreferrer noopener"
              className="font-semibold text-accent underline"
              style={{ wordBreak: 'break-word' }}
            >
              {fragment.texte}
            </a>
          ) : (
            <span key={rang}>{fragment.texte}</span>
          ),
        )}
      </p>
    )
  }

  return (
    <>
      <textarea
        value={texte}
        onChange={(evenement) => onBrouillon(evenement.target.value)}
        // Enregistré aussi à la sortie du champ, pour qui touche ailleurs sans passer par la
        // coche. La coche, elle, ne compte plus dessus : sur un écran tactile elle ne la
        // déclenche pas.
        onBlur={() => {
          if (texte !== (bloc.text ?? '')) {
            void enregistrer({ text: texte })
          }
        }}
        placeholder="Écrivez ici."
        rows={Math.max(3, texte.split('\n').length)}
        className="w-full rounded-field border border-border bg-card p-3 outline-none"
        style={{
          fontSize: TAILLES[bloc.textSize ?? 'normal'],
          color: bloc.textColor ? `var(--color-texte-${bloc.textColor})` : undefined,
        }}
      />

      {/* Trois tailles, prises dans l'échelle typographique du cahier (section 03.3). Un choix
          libre en pixels produirait des fiches illisibles sur un téléphone. */}
      <div className="mt-2 flex gap-2">
        {(['petit', 'normal', 'grand'] as const).map((taille) => (
          <button
            key={taille}
            type="button"
            aria-pressed={(bloc.textSize ?? 'normal') === taille}
            onClick={() => void enregistrer({ textSize: taille })}
            className="rounded-field border px-3 text-meta"
            style={{
              height: 32,
              borderColor:
                (bloc.textSize ?? 'normal') === taille
                  ? 'var(--color-accent)'
                  : 'var(--color-border)',
              background:
                (bloc.textSize ?? 'normal') === taille
                  ? 'var(--color-accent-soft)'
                  : 'var(--color-card)',
            }}
          >
            {taille === 'petit' ? 'Petit' : taille === 'grand' ? 'Grand' : 'Normal'}
          </button>
        ))}
      </div>

      {/* Cinq teintes, et l'encre ordinaire. Chacune a deux valeurs — une par thème — et toutes
          passent le seuil de contraste AA sur la carte, clair comme sombre : c'est vérifié par
          les tests de la palette, pas par l'œil. */}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-label="Couleur ordinaire"
          aria-pressed={bloc.textColor === null}
          onClick={() => void enregistrer({ textColor: null })}
          className="rounded-full border"
          style={{
            width: 28,
            height: 28,
            background: 'var(--color-ink)',
            borderColor: bloc.textColor === null ? 'var(--color-accent)' : 'var(--color-border)',
            borderWidth: bloc.textColor === null ? 3 : 1,
          }}
        />
        {(['rouge', 'vert', 'bleu', 'violet', 'ambre'] as const).map((ton) => (
          <button
            key={ton}
            type="button"
            aria-label={`Texte en ${ton}`}
            aria-pressed={bloc.textColor === ton}
            onClick={() => void enregistrer({ textColor: ton })}
            className="rounded-full border"
            style={{
              width: 28,
              height: 28,
              background: `var(--color-texte-${ton})`,
              borderColor: bloc.textColor === ton ? 'var(--color-accent)' : 'var(--color-border)',
              borderWidth: bloc.textColor === ton ? 3 : 1,
            }}
          />
        ))}
      </div>
    </>
  )
}

/** Les trois tailles, en pixels de l'échelle du cahier : méta, corps, titre de carte. */
const TAILLES: Record<BlockTextSize, number> = {
  petit: 14,
  normal: 17,
  grand: 21,
}

/** Une liste à cocher. Cocher une ligne ne touche qu'elle. */
function Cases({
  spaceId,
  itemId,
  bloc,
  edition,
  onError,
  onDone,
}: {
  spaceId: string
  itemId: string
  bloc: ItemBlock
  edition: boolean
  onError: (cause: unknown) => void
  onDone: () => Promise<void>
}) {
  const [nouvelle, setNouvelle] = useState('')
  const queryClient = useQueryClient()

  // Cocher change l'écran tout de suite, la requête suit. Le serveur répond en quelques
  // millisecondes, mais le trajet jusqu'à lui coûte bien davantage : c'est ce temps mort qu'on
  // ressentait, et non un calcul lent.
  const cocher = useMutation({
    mutationFn: (ligne: { id: string; isChecked: boolean }) =>
      api.patchBlockLine(spaceId, itemId, bloc.id, ligne.id, { isChecked: !ligne.isChecked }),
    onMutate: async (ligne) => {
      await queryClient.cancelQueries({ queryKey: ['fiche', spaceId, itemId] })

      const precedent = queryClient.getQueryData<Item>(['fiche', spaceId, itemId])

      if (precedent) {
        queryClient.setQueryData<Item>(['fiche', spaceId, itemId], {
          ...precedent,
          blocks: precedent.blocks.map((autre) =>
            autre.id !== bloc.id
              ? autre
              : {
                  ...autre,
                  lines: autre.lines.map((candidate) =>
                    candidate.id === ligne.id
                      ? { ...candidate, isChecked: !candidate.isChecked }
                      : candidate,
                  ),
                },
          ),
        })
      }

      navigator.vibrate?.(10)

      return { precedent }
    },
    onError: (cause, _ligne, contexte) => {
      if (contexte?.precedent) {
        queryClient.setQueryData(['fiche', spaceId, itemId], contexte.precedent)
      }

      onError(cause)
    },
    onSettled: onDone,
  })

  const ajouter = useMutation({
    mutationFn: () => api.addBlockLine(spaceId, itemId, bloc.id, nouvelle.trim()),
    onSuccess: async () => {
      setNouvelle('')
      await onDone()
    },
    onError,
  })

  return (
    <>
      <ul className="flex flex-col gap-1">
        {bloc.lines.map((ligne) => (
          <li key={ligne.id} className="flex items-center gap-3">
            <button
              type="button"
              role="checkbox"
              aria-checked={ligne.isChecked}
              aria-label={ligne.isChecked ? `Décocher ${ligne.label}` : `Cocher ${ligne.label}`}
              onClick={() => cocher.mutate(ligne)}
              className="flex shrink-0 items-center justify-center rounded-field"
              style={{
                width: 28,
                height: 28,
                border: `1.5px solid ${ligne.isChecked ? 'var(--color-accent)' : 'var(--color-border)'}`,
                background: ligne.isChecked ? 'var(--color-accent)' : 'var(--color-card)',
                color: 'var(--color-card)',
              }}
            >
              {ligne.isChecked ? '✓' : ''}
            </button>

            {/* Pas de texte barré ici, contrairement à la liste de courses. Là-bas, barrer dit
                « pris, on n'y revient pas » ; sur une fiche, une liste cochée se relit — et du
                texte barré se lit mal (correction S91). La case suffit à dire l'état. */}
            <span className="grow text-body">{ligne.label}</span>

            {edition ? (
              <button
                type="button"
                onClick={async () => {
                  try {
                    await api.deleteBlockLine(spaceId, itemId, bloc.id, ligne.id)
                    await onDone()
                  } catch (cause) {
                    onError(cause)
                  }
                }}
                aria-label={`Retirer ${ligne.label}`}
                className="shrink-0 text-meta text-ink-muted"
                style={{ width: 28 }}
              >
                ✕
              </button>
            ) : null}
          </li>
        ))}
      </ul>

      {/* Le champ d'ajout n'apparaît qu'en retouche : une liste finie se lit, elle ne se
          présente pas comme un formulaire. Cocher, lui, reste possible en permanence — c'est
          tout l'objet d'une liste à cocher. */}
      {edition ? (
      <form
        className="mt-2 flex gap-2"
        onSubmit={(evenement) => {
          evenement.preventDefault()

          if (nouvelle.trim().length > 0) {
            ajouter.mutate()
          }
        }}
      >
        <input
          value={nouvelle}
          onChange={(evenement) => setNouvelle(evenement.target.value)}
          placeholder="Ajouter une ligne"
          className="grow rounded-field border border-border bg-card px-3 outline-none"
          style={{ height: 44, fontSize: 16 }}
        />
        <button
          type="submit"
          disabled={nouvelle.trim().length === 0 || ajouter.isPending}
          className="shrink-0 rounded-field bg-accent px-4 font-semibold text-card disabled:opacity-40"
          style={{ height: 44 }}
        >
          +
        </button>
      </form>
      ) : null}
    </>
  )
}

/**
 * Une image, prise parmi les pièces jointes de la fiche.
 *
 * L'envoi passe par le stockage qui existe déjà : il réduit l'image et efface ses métadonnées,
 * position GPS comprise. En ouvrir un second aurait dupliqué ce soin, et un seul l'aurait gardé.
 */
function Image({
  spaceId,
  itemId,
  bloc,
  onError,
  onDone,
}: {
  spaceId: string
  itemId: string
  bloc: ItemBlock
  onError: (cause: unknown) => void
  onDone: () => Promise<void>
}) {
  const [envoi, setEnvoi] = useState(false)

  if (bloc.imageUrl) {
    return (
      <>
        <img src={bloc.imageUrl} alt="" loading="lazy" className="w-full rounded-field" />
        <button
          type="button"
          onClick={async () => {
            try {
              await api.updateBlock(spaceId, itemId, bloc.id, {
                attachmentId: null,
                title: bloc.title,
              })
              await onDone()
            } catch (cause) {
              onError(cause)
            }
          }}
          className="mt-2 text-meta font-semibold text-ink-muted"
        >
          Retirer l'image
        </button>
      </>
    )
  }

  return (
    <>
      <ChoixDImage
        libelle="Choisir une image"
        disabled={envoi}
        onChoix={async (choisis) => {
          const fichier = choisis[0]

          if (!fichier) {
            return
          }

          setEnvoi(true)

          try {
            const image = await api.uploadImage(spaceId, itemId, fichier)
            await api.updateBlock(spaceId, itemId, bloc.id, {
              attachmentId: image.id,
              title: bloc.title,
            })
            await onDone()
          } catch (cause) {
            onError(cause)
          } finally {
            setEnvoi(false)
          }
        }}
      />
      {envoi ? <p className="mt-2 text-meta text-ink-muted">Envoi…</p> : null}
    </>
  )
}

/** Un lien, avec son libellé. Ouvrable et copiable, comme partout ailleurs. */
function Lien({
  spaceId,
  itemId,
  bloc,
  onError,
  onDone,
}: {
  spaceId: string
  itemId: string
  bloc: ItemBlock
  onError: (cause: unknown) => void
  onDone: () => Promise<void>
}) {
  const [edition, setEdition] = useState(bloc.url === null)
  const [url, setUrl] = useState(bloc.url ?? '')
  const [libelle, setLibelle] = useState(bloc.text ?? '')
  const [copie, setCopie] = useState<'oui' | 'non' | null>(null)

  if (edition) {
    return (
      <>
        <input
          value={url}
          onChange={(evenement) => setUrl(evenement.target.value)}
          inputMode="url"
          placeholder="https://…"
          className="w-full rounded-field border border-border bg-card px-3 outline-none"
          style={{ height: 44, fontSize: 16 }}
        />
        <input
          value={libelle}
          onChange={(evenement) => setLibelle(evenement.target.value)}
          placeholder="Comment l'appeler (facultatif)"
          className="mt-2 w-full rounded-field border border-border bg-card px-3 outline-none"
          style={{ height: 44, fontSize: 16 }}
        />
        <button
          type="button"
          disabled={url.trim().length === 0}
          onClick={async () => {
            try {
              await api.updateBlock(spaceId, itemId, bloc.id, {
                url: url.trim(),
                text: libelle.trim(),
                title: bloc.title,
              })
              setEdition(false)
              await onDone()
            } catch (cause) {
              onError(cause)
            }
          }}
          className="mt-2 w-full rounded-button bg-accent font-semibold text-card disabled:opacity-40"
          style={{ height: 44 }}
        >
          Enregistrer
        </button>
      </>
    )
  }

  return (
    <>
      <a
        href={bloc.url ?? '#'}
        target="_blank"
        rel="noreferrer noopener"
        className="block font-semibold text-accent underline"
        style={{ wordBreak: 'break-word' }}
      >
        {bloc.text && bloc.text.length > 0
          ? bloc.text
          : decouperEnLiens(bloc.url ?? '')
              .map((fragment) => fragment.texte)
              .join('')}
      </a>

      <div className="mt-2 flex gap-4">
        <button
          type="button"
          onClick={async () => setCopie((await copier(bloc.url ?? '')) ? 'oui' : 'non')}
          className="text-meta font-semibold text-ink-muted"
        >
          {copie === 'oui' ? 'Lien copié' : copie === 'non' ? 'Copie impossible' : 'Copier'}
        </button>
        <button
          type="button"
          onClick={() => setEdition(true)}
          className="text-meta font-semibold text-ink-muted"
        >
          Modifier
        </button>
      </div>
    </>
  )
}
